import {
  createEmailTemplate,
  deleteEmailTemplate,
  type EmailTemplateKey,
  type EmailTemplateResponse,
  getEmailTemplateByKey,
  type Language,
  orUndefined,
  renderMjmlPreview,
  updateEmailTemplate,
} from '@lms/api-client/backend';

// Ports app/[brand]/admin/email/_components/actions.ts. The Next server actions
// (`addEmailTemplate`, `renderMjml`, `saveTemplate`, `removeEmailTemplate`)
// become plain async functions the SPA calls directly. `revalidatePath` and the
// server-side `getCurrentSite`/`getSlug` are gone: the caller supplies the site
// id and revalidates its SWR cache with `mutate` after a mutation succeeds.

export interface AddEmailTemplateInput {
  template: EmailTemplateKey;
  language: Language;
  siteId: number;
}

export type AddEmailTemplateResult =
  | { ok: true }
  | { ok: false; message: string };

// The Next action looked up the global base template by key + language and
// created a site-scoped copy for the current site.
export async function addEmailTemplate(
  input: AddEmailTemplateInput,
): Promise<AddEmailTemplateResult> {
  try {
    const baseTemplate = await orUndefined(
      getEmailTemplateByKey(input.template, null, input.language),
    );
    if (baseTemplate === undefined) {
      throw new Error('Missing base template');
    }
    await createEmailTemplate({
      key: baseTemplate.key,
      language: baseTemplate.language,
      siteId: input.siteId,
      template: baseTemplate.template,
      subject: baseTemplate.subject,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: `${e}` };
  }
}

export interface CodeError {
  line: number;
  message: string;
  tagName: string;
  formattedMessage: string;
}

export async function renderMjml(
  template: EmailTemplateResponse,
  variables: Record<string, string>,
): Promise<{ html: string; errors: CodeError[]; subject: string }> {
  try {
    const result = await renderMjmlPreview({
      template: template.template,
      subject: template.subject,
      variables,
    });

    // Convert backend errors to frontend format
    const errors: CodeError[] = result.errors.map((msg, i) => ({
      line: i + 1,
      message: msg,
      tagName: '',
      formattedMessage: msg,
    }));

    return {
      html: result.html,
      errors,
      subject: result.subject,
    };
  } catch (e) {
    // If backend call fails, return error in the expected format
    console.error('Failed to render MJML:', e);
    return {
      html: '',
      errors: [
        {
          line: 0,
          message: e instanceof Error ? e.message : 'Failed to render template',
          tagName: '',
          formattedMessage:
            e instanceof Error ? e.message : 'Failed to render template',
        },
      ],
      subject: template.subject,
    };
  }
}

export async function saveTemplate(
  id: number,
  template: string,
  subject: string,
): Promise<void> {
  await updateEmailTemplate(id, { template, subject });
}

export type RemoveEmailTemplateResult =
  | { ok: true }
  | { ok: false; error: string };

export async function removeEmailTemplate(
  id: number,
): Promise<RemoveEmailTemplateResult> {
  try {
    await deleteEmailTemplate(id);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}
